import {
  useId,
  useRef,
  useState,
  type CSSProperties,
  type FocusEvent,
  type PointerEvent,
} from "react";
import type { FunnelStageDatum, FunnelTransitionDatum } from "@bi/shared";
import type { FunnelDisplay } from "@bi/shared/types";
import { layoutFunnelFlow } from "../lib/funnel-flow";
import { formatCompactValue, formatNumber } from "../lib/number-format";

const COLORS: Record<string, string> = {
  created: "var(--rc-chart-1)",
  engaged: "var(--rc-chart-7)",
  brief: "var(--rc-chart-5)",
  brief_done: "var(--rc-chart-2)",
  searched: "var(--rc-chart-4)",
  offers: "var(--rc-chart-3)",
  finalists: "var(--rc-chart-6)",
  picked: "var(--rc-chart-1)",
  seller_replied: "var(--rc-chart-7)",
  fiverr_offer: "var(--rc-chart-2)",
  ordered: "var(--rc-chart-6)",
  completed: "var(--rc-color-success)",
  cancelled: "var(--rc-color-danger)",
  mixed: "var(--rc-color-warning)",
  unresolved: "var(--rc-chart-1)",
};
const OUTCOMES: Record<string, readonly [string, string]> = {
  completed: ["Completed", "No cancelled orders"],
  cancelled: ["Cancelled", "No completed orders"],
  mixed: ["Completed + cancelled", "Different orders on one project"],
  unresolved: ["Pending outcome", "Pending or unreported status"],
};
const color = (key: string) => COLORS[key] ?? "var(--rc-chart-1)";
const percent = (n: number, total: number) =>
  `${total > 0 ? ((100 * n) / total).toFixed(1) : "0"}%`;

export function FunnelFlow({
  display,
  stages,
  transitions,
}: {
  display: FunnelDisplay;
  stages: readonly FunnelStageDatum[];
  transitions: readonly FunnelTransitionDatum[];
}) {
  const [showDrops, setShowDrops] = useState(true);
  const [splitGuests, setSplitGuests] = useState(true);
  const [tip, setTip] = useState<{ text: string; x: number; y: number } | null>(null);
  const host = useRef<HTMLDivElement>(null);
  const id = useId().replace(/:/g, "");
  const split = !!display.secondary_reached_column && splitGuests;
  const graph = layoutFunnelFlow(
    split ? stages : stages.map((s) => ({ ...s, guestReached: 0, guestStalled: 0 })),
    split ? transitions : transitions.map((e) => ({ ...e, guestCount: 0 })),
    display.outcome_stage_keys ?? [],
  );
  const main = graph.nodes.filter((n) => !n.terminal);
  const outcomes = graph.nodes.filter((n) => n.terminal);
  const cohort = stages[0]?.reached ?? 0;
  const ordered = main.at(-1)?.reached ?? 0;
  const compact = (n: number) => formatCompactValue(n, display.value_format);
  const exact = (n: number) => formatNumber(n, display.value_format) ?? n.toLocaleString("en-US");
  const details = (key: string, drop = false) => {
    const s = stages.find((stage) => stage.key === key)!;
    const value = drop ? s.stalled : s.reached,
      guests = drop ? s.guestStalled : s.guestReached;
    return (
      `${s.label}${drop ? ": drop-off" : ""}\n${exact(value)} ${display.unit_label} · ${percent(value, drop ? s.reached : cohort)} of ${drop ? "this stage" : "the cohort"}` +
      (display.secondary_reached_column
        ? `\nSigned in: ${exact(value - guests)}\nGuest: ${exact(guests)}`
        : "") +
      (drop ? "\nNo later displayed stage observed yet." : "")
    );
  };
  const showTip = (text: string, event: PointerEvent<Element> | FocusEvent<Element>) => {
    const bounds = host.current!.getBoundingClientRect();
    const target = event.currentTarget.getBoundingClientRect();
    const x = "clientX" in event ? event.clientX : target.x + target.width / 2;
    const y = "clientY" in event ? event.clientY : target.y;
    setTip({
      text,
      x:
        bounds.width < 340
          ? bounds.width / 2
          : Math.max(165, Math.min(bounds.width - 165, x - bounds.x)),
      y: y - bounds.y - 12,
    });
  };
  const interactive = (text: string) => ({
    tabIndex: 0,
    "aria-label": text,
    onPointerMove: (e: PointerEvent<Element>) => showTip(text, e),
    onPointerLeave: () => setTip(null),
    onFocus: (e: FocusEvent<Element>) => showTip(text, e),
    onBlur: () => setTip(null),
  });
  return (
    <div className="bi-flow" ref={host}>
      <div className="bi-flow__toolbar">
        <div className="bi-flow__legend">
          <span>
            <i />
            {split ? "Signed in" : "Total"}
          </span>
          {split && (
            <span>
              <i className="bi-flow__hatch" />
              Guest
            </span>
          )}
          <span>
            <i className="bi-flow__skip-key" />
            Direct route
          </span>
          {showDrops && (
            <span>
              <i className="bi-flow__drop-key" />
              Drop-off
            </span>
          )}
        </div>
        <div className="bi-flow__controls">
          {!!display.secondary_reached_column && (
            <label>
              <input
                type="checkbox"
                checked={splitGuests}
                onChange={(e) => setSplitGuests(e.target.checked)}
              />
              Split guests
            </label>
          )}
          <label>
            <input
              type="checkbox"
              checked={showDrops}
              onChange={(e) => setShowDrops(e.target.checked)}
            />
            Show drop-offs
          </label>
        </div>
      </div>
      <svg
        className="bi-flow__svg"
        viewBox={`0 0 ${graph.width} ${showDrops ? graph.height : 355}`}
        role="img"
        aria-label={`Project lifecycle in ${display.unit_label}`}
      >
        <defs>
          <pattern id={`${id}-guest`} patternUnits="userSpaceOnUse" width="6" height="6">
            <rect width="6" height="6" fill="var(--rc-color-surface)" />
            <path
              d="M -1 1 L 1 -1 M 0 6 L 6 0 M 5 7 L 7 5"
              stroke="var(--rc-color-text-muted)"
              strokeWidth="1.5"
            />
          </pattern>
          <linearGradient id={`${id}-drop`} x1="0" y1="0" x2="0" y2="1">
            <stop stopColor="var(--rc-color-danger)" stopOpacity=".3" />
            <stop offset="1" stopColor="var(--rc-color-danger)" stopOpacity=".06" />
          </linearGradient>
          {graph.edges.map((edge, i) => (
            <linearGradient id={`${id}-edge-${i}`} key={i}>
              <stop stopColor={color(edge.fromKey)} stopOpacity=".38" />
              <stop offset="1" stopColor={color(edge.toKey)} stopOpacity=".5" />
            </linearGradient>
          ))}
        </defs>
        {showDrops &&
          graph.drops.map((drop) => (
            <g key={drop.key}>
              <path d={drop.path} fill={`url(#${id}-drop)`} />
              <path d={drop.guestPath} fill={`url(#${id}-guest)`} opacity=".25" />
              <path
                d={`${drop.path} ${drop.guestPath}`}
                fill="transparent"
                {...interactive(details(drop.key, true))}
              />
            </g>
          ))}
        {graph.edges
          .filter((edge) => edge.path || edge.guestPath)
          .map((edge) => {
            const i = graph.edges.indexOf(edge);
            const text = `${stages.find((s) => s.key === edge.fromKey)!.label} → ${stages.find((s) => s.key === edge.toKey)!.label}\n${exact(edge.count)} ${display.unit_label}${split ? `\nSigned in: ${exact(edge.count - edge.guestCount)}\nGuest: ${exact(edge.guestCount)}` : ""}${edge.skipped ? "\nDirect route past unobserved stages" : ""}`;
            return (
              <g key={i}>
                <path
                  d={edge.path}
                  fill={edge.skipped ? "var(--rc-chart-1)" : `url(#${id}-edge-${i})`}
                  opacity={edge.skipped ? 0.25 : 1}
                />
                <path d={edge.guestPath} fill={`url(#${id}-guest)`} opacity=".5" />
                <path
                  d={`${edge.path} ${edge.guestPath}`}
                  fill="transparent"
                  {...interactive(text)}
                />
              </g>
            );
          })}
        {main.map((node) => {
          const x = node.x + 6;
          const anchor = "middle";
          return (
            <g key={node.key}>
              <text x={x} y="22" textAnchor={anchor} className="bi-flow__label">
                {node.label
                  .split(" ")
                  .reduce<string[]>((lines, word) => {
                    const last = lines.at(-1);
                    if (last && last.length + word.length < 16)
                      lines[lines.length - 1] += ` ${word}`;
                    else lines.push(word);
                    return lines;
                  }, [])
                  .map((line, j) => (
                    <tspan key={j} x={x} dy={j ? 17 : 0}>
                      {line}
                    </tspan>
                  ))}
              </text>
              <rect
                x={node.x}
                y={node.y}
                width="12"
                height={node.signedHeight > 0 ? Math.max(node.signedHeight, 1) : 0}
                fill={color(node.key)}
              />
              {node.guestHeight > 0 && (
                <rect
                  x={node.x}
                  y={node.guestY}
                  width="12"
                  height={Math.max(node.guestHeight, 1)}
                  fill={`url(#${id}-guest)`}
                  stroke={color(node.key)}
                />
              )}
              <rect
                x={node.x - 8}
                y="45"
                width="28"
                height="185"
                fill="transparent"
                {...interactive(details(node.key))}
              />
              <text x={x} y="282" textAnchor={anchor} className="bi-flow__value">
                {compact(node.reached)}
              </text>
              <text x={x} y="303" textAnchor={anchor} className="bi-flow__meta">
                {percent(node.reached, cohort)} of cohort
              </text>
              {split && (
                <text x={x} y="322" textAnchor={anchor} className="bi-flow__meta">
                  <tspan x={x}>Signed in {compact(node.reached - node.guestReached)}</tspan>
                  <tspan x={x} dy="17">
                    Guest {compact(node.guestReached)}
                  </tspan>
                </text>
              )}
              {showDrops && node.stalled > 0 && (
                <text
                  x={x}
                  y="377"
                  textAnchor={anchor}
                  className="bi-flow__drop-label"
                  {...interactive(details(node.key, true))}
                >
                  −{compact(node.stalled)} · {percent(node.stalled, node.reached)}
                </text>
              )}
            </g>
          );
        })}
      </svg>
      <ol className="bi-flow__compact">
        {main.map((node) => (
          <li key={node.key} style={{ "--flow-color": color(node.key) } as CSSProperties}>
            <div className="bi-flow__compact-head">
              <span>{node.label}</span>
              <strong>{exact(node.reached)}</strong>
            </div>
            <div className="bi-flow__track" {...interactive(details(node.key))}>
              <span style={{ width: percent(node.reached - node.guestReached, cohort) }} />
              {split && (
                <span
                  className="bi-flow__hatch"
                  style={{ width: percent(node.guestReached, cohort) }}
                />
              )}
            </div>
            <div className="bi-flow__compact-meta">
              <span>
                {split
                  ? `Signed in ${compact(node.reached - node.guestReached)} · Guest ${compact(node.guestReached)}`
                  : `${percent(node.reached, cohort)} of cohort`}
              </span>
              {showDrops && node.stalled > 0 && (
                <span className="bi-flow__drop-label">
                  −{compact(node.stalled)} ({percent(node.stalled, node.reached)})
                </span>
              )}
            </div>
          </li>
        ))}
      </ol>
      <div className="bi-flow__outcome-heading">
        <strong>Order outcomes</strong>
        <span>
          {exact(ordered)} {display.unit_label} across four exclusive branches
        </span>
      </div>
      <div className="bi-flow__outcomes">
        {outcomes.map((node) => (
          <div
            key={node.key}
            className="bi-flow__outcome"
            style={{ "--flow-color": color(node.key) } as CSSProperties}
            {...interactive(details(node.key))}
          >
            <span className="bi-flow__outcome-label">{OUTCOMES[node.key]?.[0] ?? node.label}</span>
            <strong>{exact(node.reached)}</strong>
            <span>{percent(node.reached, ordered)} of orders stage</span>
            <small>{OUTCOMES[node.key]?.[1]}</small>
            {split && (
              <div className="bi-flow__outcome-split">
                <span>
                  Signed in <b>{compact(node.reached - node.guestReached)}</b>
                </span>
                <span>
                  Guest <b>{compact(node.guestReached)}</b>
                </span>
              </div>
            )}
          </div>
        ))}
      </div>
      <p className="bi-flow__hint">
        Drop-off = no later displayed stage observed yet. Hover or focus a stage or path for exact
        values.
      </p>
      {tip && (
        <div className="bi-flow__tooltip" role="tooltip" style={{ left: tip.x, top: tip.y }}>
          {tip.text}
        </div>
      )}
    </div>
  );
}
